import styled from "@emotion/styled";
import { doctorRegistrationPage, toDoctorProfileHomePage } from "Doctor/Router";
import { homePage } from "Home/routes";
import { useAuthBroadcastSignal } from "Session";
import { useStore } from "effector-react";
import { memo } from "react";
import { Helmet } from "react-helmet";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Block,
  Button,
  Flex,
  Grid,
  HeroBanner,
  Input,
  LoaderWithOverlay,
  Logo,
  PasswordInput,
  Typography,
  UnifiedLink,
  color,
  errorNotification,
} from "ui-kit";
import { logIn } from "./doctorSession";
import loginBg from "./login-bg.jpg";

type LoginData = {
  email: string;
  password: string;
};

export const LoginPage = memo(() => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useStore(logIn.pending);

  const { notifyOthersAboutLogIn } = useAuthBroadcastSignal();

  const logInDoctor: SubmitHandler<LoginData> = ({ email, password }) => {
    if (isLoading) {
      // prevent multiple request sending
      // Therefore, generated session token stays valid
      return;
    }
    logIn({
      email,
      password,
    })
      .then(({ id: doctorId }) => {
        // Use case: when user tried to access protected route,
        // redirect him back to that route after authentication
        const { from } = location.state || {
          from: { pathname: toDoctorProfileHomePage(doctorId) },
        };
        navigate(from);
      })
      .then(notifyOthersAboutLogIn)
      .catch((error: Error) => {
        errorNotification(error.message);
      });
  };
  return (
    <Grid gridTemplateColumns="30% 70%" minHeight="100vh">
      <Helmet>
        <title>Doctor login</title>
      </Helmet>
      <LoginFormContainer>
        {isLoading && <LoaderWithOverlay />}
        <LoginForm flexDirection="column" justifyContent="space-between">
          <Block textAlign="left">
            <Link to={homePage}>
              <Logo fill={color.G200} width="188px" />
            </Link>
          </Block>
          <Flex alignItems="center">
            <LoginFormSection width="100%">
              <Block marginBottom={2} textAlign="center">
                <Typography as="h1" type="h2">
                  Log in
                </Typography>
              </Block>
              <form onSubmit={handleSubmit(logInDoctor)}>
                <Block marginBottom={3}>
                  <Input
                    autoFocus
                    type="email"
                    label="Email"
                    errorMessage={errors["email"]?.message}
                    hasErrors={!!errors["email"]}
                    placeholder="Email"
                    isRequired
                    {...register("email", { required: "Please, enter email" })}
                  />
                </Block>
                <Block marginBottom={4}>
                  <PasswordInput
                    label="Password"
                    errorMessage={errors["password"]?.message}
                    hasErrors={!!errors["password"]}
                    placeholder="Password"
                    isRequired
                    {...register("password", {
                      required: "Please, enter password",
                    })}
                  />
                </Block>
                <Block textAlign="center">
                  <Button filled>Log in</Button>
                </Block>
              </form>
            </LoginFormSection>
          </Flex>
          <Block>
            <Typography type="h4">Do not have an account?</Typography>{" "}
            <UnifiedLink to={doctorRegistrationPage} type="h4">
              Sign up
            </UnifiedLink>
          </Block>
        </LoginForm>
      </LoginFormContainer>
      <Background enableOverlay image={loginBg}>
        <AdvantagesSection
          gridTemplateColumns="100%"
          gridTemplateRows="max-content auto max-content"
          height="100%"
        >
          <AdvantagesListContainer
            gridTemplateColumns="repeat(4, 1fr)"
            gridColumnGap={10}
            paddingLeft={3}
          >
            <Advantage type="h2" fontColor="N0">
              Security and privacy
            </Advantage>
            <Advantage type="h2" fontColor="N0">
              Easy to use system
            </Advantage>
            <Advantage type="h2" fontColor="N0">
              Flexible schedule
            </Advantage>
            <Advantage type="h2" fontColor="N0">
              Earnings as in the clinic
            </Advantage>
          </AdvantagesListContainer>
          <Invitation>
            Log in now and help patients start and maintain a healthy lifestyle
            and be happy!
          </Invitation>
          <Disclaimer paddingLeft={6}>
            <Typography fontSize="20px" fontColor="N0" fontWeight={400}>
              The doctor must remember that the patient trusts you with his
              life, so the patient&apos;s life is in your hands, and you are
              responsible for it.
            </Typography>
          </Disclaimer>
        </AdvantagesSection>
      </Background>
    </Grid>
  );
});

const Background = styled(HeroBanner)`
  height: 100%;
  .overlay {
    opacity: 0.62;
  }
`;

const LoginFormContainer = styled(Flex)`
  background-color: ${color.N0};
  position: relative;
`;

const LoginForm = styled(Flex)`
  background-color: ${color.N0};
  padding: 72px 72px 32px;
  width: 100%;
`;

const Disclaimer = styled(Block)`
  grid-column: 1 / end;
  grid-row: 3 / 3;
`;

const LoginFormSection = Block.withComponent("section");

const AdvantagesSection = styled(Grid.withComponent("section"))`
  align-content: space-between;
  padding: 72px 56px 32px 72px;
`;

const AdvantagesListContainer = styled(Grid)`
  grid-column: 1 / end;
  grid-row: 1 / 1;
`;

const Advantage = styled(Typography)`
  align-self: center;
  padding-left: 32px;
  position: relative;
  &::before {
    border-radius: 50%;
    background-color: ${color.G200};
    content: "";
    display: inline-block;
    left: 0;
    margin-top: -10px;
    top: 50%;
    position: absolute;
    height: 20px;
    width: 20px;
  }
`;

const Invitation = styled(Block)`
  align-self: center;
  color: ${color.N0};
  font-family: Roboto, Arial, sans-serif;
  font-size: 50px;
  font-weight: 700;
  grid-column: 1 / end;
  grid-row: 2 / 3;
  line-height: 72px;
  padding-left: 48px;
`;
